import type { ReactNode } from 'react';
import { useTranslate } from 'react-admin';
import { Card, CardContent, Box, Typography } from '@mui/material';
import { Assignment } from '@mui/icons-material';
import { ReportBody } from './ReportBody';
import { HandoffActions } from './HandoffActions';
import type { InvestigationReport } from './types';

interface ReportCardProps {
  report: InvestigationReport;
  // Phase-2 injection host: sub-plan 18 injects its PromoteToPlaybookButton into this stable
  // findings-branch action row (14 owns the host markup; 18 owns the injection edit). Keep the
  // row present even when empty so the downstream injection is not an orphan.
  actions?: ReactNode;
}

// Terminal report card: refusal branch vs findings branch (via ReportBody), plus HandoffActions
// when handoffMarkdown is present, plus a stable action row for Phase-2 injection.
export const ReportCard = ({ report, actions }: ReportCardProps) => {
  const translate = useTranslate();
  const showHandoff =
    typeof report.handoffMarkdown === 'string' && report.handoffMarkdown.length > 0;
  const isFindings = report.status === 'completed';

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 } }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Assignment sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {translate('aiTroubleshooting.report.title')}
          </Typography>
        </Box>

        <ReportBody data={report} />

        {/* Phase-2 injection host: stable findings-branch action row (sub-plan 18). */}
        {isFindings && actions ? (
          <Box
            data-testid="report-card-actions"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}
          >
            {actions}
          </Box>
        ) : null}

        {/* Gate purely on handoffMarkdown presence, not the category string (REF-06 s.1/s.5). */}
        {showHandoff && (
          <HandoffActions
            markdown={report.handoffMarkdown as string}
            investigationId={report.investigationId}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ReportCard;
